import React, { useState } from 'react';
import { SettingsCard } from './shared/SettingsCard';
import { SettingsToggle } from './shared/SettingsToggle';
import { Shield, UserX, AlertCircle, CheckCircle } from 'lucide-react';
interface PrivacySectionProps {
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}
export const PrivacySection: React.FC<PrivacySectionProps> = ({
  setHasUnsavedChanges
}) => {
  const [privacy, setPrivacy] = useState({
    showActivity: true,
    showSubscriptionOffers: true,
    limitDmsToSubscribers: false,
    ageVerified: true,
    blockedUsers: [{
      id: 1,
      username: 'toxic_user123',
      blockedDate: '2023-10-15'
    }, {
      id: 2,
      username: 'spammer_account',
      blockedDate: '2023-11-02'
    }]
  });
  const [showBlockUserModal, setShowBlockUserModal] = useState(false);
  const [newBlockUsername, setNewBlockUsername] = useState('');
  const handleToggleChange = (field: string, value: boolean) => {
    setPrivacy(prev => ({
      ...prev,
      [field]: value
    }));
    setHasUnsavedChanges(true);
  };
  const handleUnblockUser = (id: number) => {
    setPrivacy(prev => ({
      ...prev,
      blockedUsers: prev.blockedUsers.filter(user => user.id !== id)
    }));
    setHasUnsavedChanges(true);
  };
  const handleBlockUser = () => {
    if (newBlockUsername.trim()) {
      const newUser = {
        id: Date.now(),
        username: newBlockUsername.trim(),
        blockedDate: new Date().toISOString().split('T')[0]
      };
      setPrivacy(prev => ({
        ...prev,
        blockedUsers: [...prev.blockedUsers, newUser]
      }));
      setNewBlockUsername('');
      setShowBlockUserModal(false);
      setHasUnsavedChanges(true);
    }
  };
  return <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-18736">
      <SettingsCard title="Privacy Settings" description="Control who can see your activity and how you interact with others." data-id="element-18737">
        <div className="space-y-4" data-id="element-18738">
          <SettingsToggle id="show-activity" label="Show Activity Status" description="Allow others to see when you're online or recently active." checked={privacy.showActivity} onChange={checked => handleToggleChange('showActivity', checked)} tooltip="When enabled, other users can see when you're online or when you were last active." data-id="element-18739" />
          <SettingsToggle id="show-subscription-offers" label="Show Subscription Offers" description="Receive notifications about special offers and promotions." checked={privacy.showSubscriptionOffers} onChange={checked => handleToggleChange('showSubscriptionOffers', checked)} data-id="element-18740" />
          <SettingsToggle id="limit-dms" label="Limit DMs to Subscribers Only" description="Only allow users who subscribe to your content to send you direct messages." checked={privacy.limitDmsToSubscribers} onChange={checked => handleToggleChange('limitDmsToSubscribers', checked)} tooltip="When enabled, only users who have subscribed to your content can send you direct messages." data-id="element-18741" />
        </div>
      </SettingsCard>
      <SettingsCard title="Blocked Users" description="Manage users you've blocked from interacting with you." data-id="element-18742">
        <div className="mb-4" data-id="element-18743">
          <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm flex items-center gap-2" onClick={() => setShowBlockUserModal(true)} data-id="element-18744">
            <UserX size={16} data-id="element-18745" />
            <span data-id="element-18746">Block a User</span>
          </button>
        </div>
        {privacy.blockedUsers.length > 0 ? <div className="space-y-3" data-id="element-18747">
            {privacy.blockedUsers.map(user => <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-18748">
                <div data-id="element-18749">
                  <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18750">
                    @{user.username}
                  </h5>
                  <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-18751">
                    Blocked on {user.blockedDate}
                  </p>
                </div>
                <button className="px-3 py-1.5 bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded text-xs" onClick={() => handleUnblockUser(user.id)} data-id="element-18752">
                  Unblock
                </button>
              </div>)}
          </div> : <p className="text-sm text-gray-500 dark:text-gray-400 py-2" data-id="element-18753">
            You haven't blocked any users yet.
          </p>}
      </SettingsCard>
      <SettingsCard title="Age Verification" description="Verify your age to access all content on SinShift." data-id="element-18754">
        <div className="flex items-center justify-between" data-id="element-18755">
          <div data-id="element-18756">
            <div className="flex items-center gap-2" data-id="element-18757">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18758">
                Age Verification Status
              </h4>
              {privacy.ageVerified ? <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full flex items-center gap-1" data-id="element-18759">
                  <CheckCircle size={12} data-id="element-18760" />
                  <span data-id="element-18761">Verified</span>
                </span> : <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 text-xs rounded-full flex items-center gap-1" data-id="element-18762">
                  <AlertCircle size={12} data-id="element-18763" />
                  <span data-id="element-18764">Not Verified</span>
                </span>}
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18765">
              {privacy.ageVerified ? 'Your age has been verified. You have full access to all content.' : 'You need to verify your age to access adult content on SinShift.'}
            </p>
          </div>
          {!privacy.ageVerified && <button className="px-3 py-1.5 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded text-sm font-medium" onClick={() => handleToggleChange('ageVerified', true)} data-id="element-18766">
              Verify Now
            </button>}
        </div>
      </SettingsCard>
      {/* Block User Modal */}
      {showBlockUserModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-id="element-18767">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl max-w-md w-full" data-id="element-18768">
            <div className="flex items-center gap-3 mb-4" data-id="element-18769">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800" data-id="element-18770">
                <UserX size={20} className="text-gray-700 dark:text-gray-300" data-id="element-18771" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-18772">
                Block a User
              </h3>
            </div>
            <p className="mb-4 text-sm text-gray-700 dark:text-gray-300" data-id="element-18773">
              Enter the username of the person you want to block. Blocked users
              won't be able to message you or interact with your content.
            </p>
            <div className="mb-4" data-id="element-18774">
              <label htmlFor="block-username" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18775">
                Username
              </label>
              <input type="text" id="block-username" value={newBlockUsername} onChange={e => setNewBlockUsername(e.target.value)} placeholder="Enter username" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-18776" />
            </div>
            <div className="flex justify-end gap-3" data-id="element-18777">
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm" onClick={() => setShowBlockUserModal(false)} data-id="element-18778">
                Cancel
              </button>
              <button className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm" onClick={handleBlockUser} disabled={!newBlockUsername.trim()} data-id="element-18779">
                Block User
              </button>
            </div>
          </div>
        </div>}
    </div>;
};